import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { TrainingProgress } from '@/hooks/use-training-progress';

import { Button } from '@/components/ui/button';
import { CircularProgress } from '@/features/training/components/circular-progress';
import { getGreeting, formatMinutes } from '@/lib/format';
import { useAuthStore } from '@/stores/auth-store';

interface WelcomeHeroProps {
  progress: TrainingProgress | undefined;
}

export function WelcomeHero({ progress }: WelcomeHeroProps) {
  const user = useAuthStore((s) => s.user);
  const completed = progress?.completed_modules ?? 0;
  const total = progress?.total_modules ?? 6;
  const remainingMinutes =
    (progress?.total_estimated_minutes ?? 60) - (progress?.completed_minutes ?? 0);

  const nextModule = progress?.modules.find((m) => m.status === 'not_started');

  return (
    <div className="flex flex-col items-center gap-6 rounded-lg border bg-card p-6 md:flex-row md:items-start">
      <CircularProgress value={completed} max={total} size={140} strokeWidth={10}>
        <span className="text-3xl font-bold">{completed}</span>
        <span className="text-xs text-muted-foreground">of {total}</span>
      </CircularProgress>
      <div className="flex-1 space-y-2 text-center md:text-left">
        <h1 className="text-2xl font-bold">
          {getGreeting()}, {user?.first_name ?? 'there'}
        </h1>
        <p className="text-muted-foreground">
          {completed === total
            ? 'Congratulations! You\'ve completed all training modules.'
            : `You've completed ${completed} of ${total} training modules. ${formatMinutes(remainingMinutes)} remaining.`}
        </p>
        {completed < total && nextModule && (
          <Button asChild className="mt-2">
            <Link to={`/training/modules/${nextModule.module_id}`}>
              Continue Training
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
